import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Import Botpoison with type assertion to avoid TypeScript errors
const Botpoison = require('@botpoison/browser');

// Create Botpoison instance
const botpoison = new Botpoison({
  publicKey: "pk_90c738f9-ee74-494d-8e7a-d48d5c347093"
});

// Business Info
const BUSINESS_INFO = {
  email: 'sales@airlinecrewtrans.com',
  phone: '+1 (650) 225-9300',
  address: '199 San Bruno Ave W, San Bruno, CA 94066',
  businessHours: {
    Monday: '24 Hours',
    Tuesday: '24 Hours',
    Wednesday: '24 Hours',
    Thursday: '24 Hours',
    Friday: '24 Hours',
    Saturday: '24 Hours',
    Sunday: '24 Hours',
  }
};


const DAY_ABBREVIATIONS: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

// Styled components
const ContactSection = styled.section`
  width: 100%;
  background-color: var(--pure-white);
  padding: 3rem 0.5rem 5rem;
  overflow: visible;
  
  @media (min-width: 640px) {
    padding: 3rem 1.5rem 5rem;
  }
  
  @media (min-width: 1024px) {
    padding: 4rem 3rem 6rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: var(--rich-black);
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--dark-gray);
  max-width: 700px;
  margin: 0 auto;
`;

const ContactCard = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: var(--pure-white);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--light-gray);
  
  @media (min-width: 640px) {
    border-radius: 24px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  border-radius: 12px;
  
  @media (min-width: 640px) {
    border-radius: 24px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormColumn = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (min-width: 640px) {
    padding: 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 3rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid var(--medium-gray);
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  color: var(--rich-black);
  background-color: var(--pure-white);
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--gold);
    box-shadow: 0 0 0 3px var(--gold-light);
  }
  
  &::placeholder {
    color: var(--dark-gray);
    opacity: 0.6;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid var(--medium-gray);
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  color: var(--rich-black);
  background-color: var(--pure-white);
  min-height: 140px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--gold);
    box-shadow: 0 0 0 3px var(--gold-light);
  }
  
  &::placeholder {
    color: var(--dark-gray);
    opacity: 0.6;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: var(--gold);
  color: var(--pure-white);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(226, 179, 61, 0.4);
  
  &:hover:not(:disabled) {
    background-color: #c9a036;
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(226, 179, 61, 0.5);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }
`;

const InfoColumn = styled.div`
  background-color: var(--off-white);
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  
  @media (min-width: 640px) {
    padding: 1rem;
  }
  
  @media (min-width: 1024px) {
    padding: 3rem;
  }
`;

const MapContainer = styled.div`
  margin-bottom: 1rem;
  
  @media (min-width: 640px) {
    margin-bottom: 2rem;
  }
`;

const MapFrame = styled.div`
  width: 100%;
  height: 16rem;
  background-color: var(--light-gray);
  overflow: hidden;
  border-radius: 0;
  
  @media (min-width: 640px) {
    border-radius: 8px;
    height: 12rem;
  }
  
  @media (min-width: 1024px) {
    height: 16rem;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem 1.5rem;
  flex-grow: 1;
  
  @media (min-width: 640px) {
    padding: 0;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const InfoIcon = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: var(--dark-gray);
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const InfoLink = styled.a`
  color: var(--dark-gray);
  text-decoration: none;
  font-size: 0.9375rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--gold);
  }
`;

const InfoText = styled.span`
  color: var(--dark-gray);
  font-size: 0.9375rem;
`;

const BusinessHoursContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const BusinessHoursContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
`;

const BusinessHoursTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--rich-black);
  margin-bottom: 0.5rem;
`;

const BusinessHoursRow = styled.div`
  display: flex;
  font-size: 0.875rem;
  color: var(--dark-gray);
  min-width: 200px;
  padding: 0.125rem 0;
`;

const BusinessHoursDay = styled.span`
  font-weight: 500;
  width: 3rem;
`;

const BusinessHoursTime = styled.span`
  margin-left: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: var(--alert-red);
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const FormErrorBanner = styled.div`
  background-color: rgba(245, 54, 92, 0.1);
  border: 1px solid var(--alert-red);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: var(--alert-red);
  font-size: 0.875rem;
`;

// Success State Components
const SuccessContainer = styled.div`
  text-align: center;
  padding: 2rem;
`;

const SuccessIconWrapper = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.25rem;
  color: var(--success-green);
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--rich-black);
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.125rem;
  color: var(--dark-gray);
  max-width: 28rem;
  margin: 0 auto;
`;

const SpinnerIcon = styled.svg`
  animation: spin 1s linear infinite;
  margin-right: 0.75rem;
  margin-left: -0.25rem;
  height: 1.25rem;
  width: 1.25rem;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SocialSection = styled.div`
  border-top: 1px solid var(--medium-gray);
  padding-top: 1.5rem;
  margin-top: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 1rem;
  
  @media (min-width: 640px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const SocialTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--rich-black);
  margin-bottom: 1rem;
`;

const SocialLinksWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SocialIconLink = styled.a<{ $color: string }>`
  width: 3rem;
  height: 3rem;
  background: var(--pure-white);
  border-radius: 50%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${props => props.$color};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

interface ContactFormProps {
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
  form?: string;
}

const ContactFormComponent: React.FC<ContactFormProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing again
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact_name_required') as string;
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact_email_required') as string;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact_email_invalid') as string;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact_subject_required') as string;
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact_message_required') as string;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await fetch('https://submit-form.com/fBQhhh8X7', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          _botpoison: solution,
          _email: {
            to: ['sales@airlinecrewtrans.com', 'warren.r.huffman@gmail.com'],
            subject: `Contact Form: ${formData.subject}`,
            from: formData.email
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors(prev => ({
        ...prev,
        form: t('contact_error', 'There was an error submitting the form. Please try again.') as string
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

  return (
    <ContactSection id="contact" className={className}>
      <Container>
        {/* Section Header */}
        <SectionHeader>
          <SectionTitle>{t('contact_title')}</SectionTitle>
          <SectionSubtitle>
            {t('contact_subtitle', 'Ready to get started? Reach out to us today and let us handle your transportation needs.')}
          </SectionSubtitle>
        </SectionHeader>

        {/* Contact Card */}
        <ContactCard>
          <CardGrid>
            {/* Form Column */}
            <FormColumn>
              {isSubmitted ? (
                <SuccessContainer>
                  <SuccessIconWrapper>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </SuccessIconWrapper>
                  <SuccessTitle>{t('contact_thank_you')}</SuccessTitle>
                  <SuccessMessage>
                    {t('contact_success_message')}
                  </SuccessMessage>
                </SuccessContainer>
              ) : (
                <>
                  {errors.form && (
                    <FormErrorBanner>{errors.form}</FormErrorBanner>
                  )}
                  <Form onSubmit={handleSubmit}>
                    <FormGroup>
                      <FormLabel htmlFor="name">{t('contact_name')}</FormLabel>
                      <FormInput
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact_name_placeholder') as string}
                        autoComplete="name"
                        autoCapitalize="words"
                      />
                      {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <FormLabel htmlFor="email">{t('contact_email')}</FormLabel>
                      <FormInput
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact_email_placeholder') as string}
                        autoComplete="email"
                        autoCapitalize="off"
                        inputMode="email"
                      />
                      {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <FormLabel htmlFor="phone">{t('contact_phone')}</FormLabel>
                      <FormInput
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact_phone_placeholder') as string}
                        autoComplete="tel"
                        inputMode="tel"
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel htmlFor="subject">{t('contact_subject')}</FormLabel>
                      <FormInput
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={t('contact_subject_placeholder') as string}
                        autoComplete="off"
                      />
                      {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <FormLabel htmlFor="message">{t('contact_message')}</FormLabel>
                      <FormTextarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact_message_placeholder') as string}
                        autoCapitalize="sentences"
                      />
                      {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                    </FormGroup>

                    <SubmitButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <SpinnerIcon fill="none" viewBox="0 0 24 24">
                            <circle
                              style={{ opacity: 0.25 }}
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              style={{ opacity: 0.75 }}
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </SpinnerIcon>
                          {t('contact_sending')}
                        </span>
                      ) : (
                        t('contact_submit')
                      )}
                    </SubmitButton>
                  </Form>
                </>
              )}
            </FormColumn>

            {/* Info Column */}
            <InfoColumn>
              {/* Map */}
              <MapContainer>
                <MapFrame>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3164.289559068282!2d-122.42294698469047!3d37.53088797980302!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9f5acdf48383%3A0x91ebd69a23112095!2s199%20San%20Bruno%20Ave%20W%2C%20San%20Bruno%2C%20CA%2094066!5e0!3m2!1sen!2sus!4v1716237118813!5m2!1sen!2sus"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Our Location"
                  />
                </MapFrame>
              </MapContainer>

              {/* Contact Info */}
              <ContactInfo>
                {/* Address */}
                <InfoItem>
                  <InfoIcon>
                    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                    </svg>
                  </InfoIcon>
                  <InfoLink
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS_INFO.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {BUSINESS_INFO.address}
                  </InfoLink>
                </InfoItem>

                {/* Phone */}
                <InfoItem>
                  <InfoIcon>
                    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                    </svg>
                  </InfoIcon>
                  <InfoLink href={`tel:${BUSINESS_INFO.phone.replace(/[^\d+]/g, '')}`}>
                    {BUSINESS_INFO.phone}
                  </InfoLink>
                </InfoItem>

                {/* Email */}
                <InfoItem>
                  <InfoIcon>
                    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    </svg>
                  </InfoIcon>
                  <InfoLink href={`mailto:${BUSINESS_INFO.email}`}>
                    {BUSINESS_INFO.email}
                  </InfoLink>
                </InfoItem>

                {/* Business Hours */}
                <BusinessHoursContainer>
                  <InfoIcon style={{ marginTop: '0.125rem' }}>
                    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                  </InfoIcon>
                  <BusinessHoursContent>
                    <BusinessHoursTitle>Business Hours</BusinessHoursTitle>
                    {days.map((day) => {
                      const hours = BUSINESS_INFO.businessHours[day];
                      return (
                        <BusinessHoursRow key={day}>
                          <BusinessHoursDay>{DAY_ABBREVIATIONS[day]}:</BusinessHoursDay>
                          <BusinessHoursTime>{hours}</BusinessHoursTime>
                        </BusinessHoursRow>
                      );
                    })}
                  </BusinessHoursContent>
                </BusinessHoursContainer>
              </ContactInfo>

              {/* Social Media Links */}
              <SocialSection>
                <SocialTitle>Follow Us</SocialTitle>
                <SocialLinksWrapper>
                  {/* Twitter/X - Main Account */}
                  <SocialIconLink 
                    href="https://twitter.com/ACrewTransport" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#000000"
                    aria-label="X (Twitter)"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </SocialIconLink>
                  
                  {/* Twitter/X - Media Account */}
                  <SocialIconLink 
                    href="https://twitter.com/mediaairlinecr1" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#000000"
                    aria-label="X (Twitter) Media"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </SocialIconLink>
                  
                  {/* LinkedIn */}
                  <SocialIconLink 
                    href="https://www.linkedin.com/in/troymak" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#0A66C2"
                    aria-label="LinkedIn"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </SocialIconLink>
                  
                  {/* Facebook */}
                  <SocialIconLink 
                    href="https://www.facebook.com/airlinecrewtrans" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#1877F2"
                    aria-label="Facebook"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </SocialIconLink>
                  
                  {/* Instagram */}
                  <SocialIconLink 
                    href="https://www.instagram.com/airlinecrewtrans/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#E4405F"
                    aria-label="Instagram"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                  </SocialIconLink>
                  
                  {/* TikTok */}
                  <SocialIconLink 
                    href="https://www.tiktok.com/@airlinecrewtrans" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    $color="#000000"
                    aria-label="TikTok"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </SocialIconLink>
                </SocialLinksWrapper>
              </SocialSection>
            </InfoColumn>
          </CardGrid>
        </ContactCard>
      </Container>
    </ContactSection>
  );
};

export default ContactFormComponent;
